import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IError, ITableState } from "../../../interfaces/base";
import {
  IGetTrainingProgramPayload,
  ITrainingProgramState,
  ICreateTrainingProgramPayload,
  IUpdateTrainingProgramPayload,
  IDeleteTrainingProgramPayload,
  IRemoveWorkoutFromProgramPayload,
  IEnableWorkoutInProgramPayload,
  ICopyWorkoutInProgramPayload,
} from "../../../interfaces/store/training-program-interface";
import { PAGE_SIZE } from "../../../common/config";

const initialState: ITrainingProgramState = {
  trainingProgram: null,
  error: null,
  loading: false,
  requestState: "prepare",
  operation: null,
  activeUsers: [],
  groups: [],
  workoutsTableState: {
    search: "",
    sort: {
      sortBy: "updatedAt",
      sortOrder: -1,
    },
    pagination: {
      page: 0,
      limit: PAGE_SIZE,
      skip: 0,
    },
  },
};

const _getTrainingProgramStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IGetTrainingProgramPayload>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "read";
};

const _getTrainingProgramSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.trainingProgram = payload.trainingProgram;
  state.groups = payload.groups;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _getTrainingProgramFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.requestState = "error";
};

const _resetTrainingProgram = (state: ITrainingProgramState) => {
  state.trainingProgram = null;
  state.error = null;
  state.loading = false;
  state.requestState = "prepare";
  state.operation = null;
  state.activeUsers = [];
  state.groups = [];
};

const _createTrainingProgramStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<ICreateTrainingProgramPayload>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "create";
};

const _createTrainingProgramSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.trainingProgram = payload;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _createTrainingProgramFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.trainingProgram = null;
  state.requestState = "error";
};

const _updateTrainingProgramStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IUpdateTrainingProgramPayload>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "update";
};

const _updateTrainingProgramSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.trainingProgram = payload;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _updateTrainingProgramFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.trainingProgram = null;
  state.requestState = "error";
};

const _deleteTrainingProgramStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IDeleteTrainingProgramPayload>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "delete";
};

const _deleteTrainingProgramSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.trainingProgram = null;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _deleteTrainingProgramFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.trainingProgram = null;
  state.requestState = "error";
};

const _removeWorkoutFromProgramStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IRemoveWorkoutFromProgramPayload>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "update";
};

const _removeWorkoutFromProgramSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.trainingProgram = payload;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _removeWorkoutFromProgramFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.requestState = "error";
};

const _toggleEnableWorkoutInTrainingProgramStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IEnableWorkoutInProgramPayload>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "update";
};

const _toggleEnableWorkoutInTrainingProgramSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.trainingProgram = payload;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _toggleEnableWorkoutInTrainingProgramFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.requestState = "error";
};

const _overrideWorkoutInTrainingProgramStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "update";
};

const _overrideWorkoutInTrainingProgramSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.trainingProgram = payload;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _overrideWorkoutInTrainingProgramFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.requestState = "error";
};

const _copyWorkoutInTrainingProgramStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<ICopyWorkoutInProgramPayload>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "update";
};

const _copyWorkoutInTrainingProgramSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  // state.trainingProgram = payload;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _copyWorkoutInTrainingProgramFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.requestState = "error";
};

const _resetTrainingProgramError = (state: ITrainingProgramState) => {
  state.error = null;
};

const _getTrainingProgramActiveUsersStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.loading = true;
  state.error = null;
};

const _getTrainingProgramActiveUsersSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<any>
) => {
  state.activeUsers = payload;
  state.loading = false;
};

const _getTrainingProgramActiveUsersFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.error = payload;
  state.loading = false;
};

const _resetTrainingProgramActiveUsers = (state: ITrainingProgramState) => {
  state.activeUsers = [];
};

const _updateWorkoutsTableState = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<ITableState>
) => {
  state.workoutsTableState = payload;
};

const _resetTrainingProgramWorkoutsTableState = (
  state: ITrainingProgramState
) => {
  state.workoutsTableState = {
    search: "",
    sort: {
      sortBy: "updatedAt",
      sortOrder: -1,
    },
    pagination: {
      page: 0,
      limit: PAGE_SIZE,
      skip: 0,
    },
  };
};

const _getGroupsStart = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<string>
) => {
  state.loading = true;
  state.error = null;
  state.requestState = "start";
  state.operation = "read";
};

const _getGroupsSuccess = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<string[]>
) => {
  state.groups = payload;
  state.loading = false;
  state.error = null;
  state.requestState = "finish";
};

const _getGroupsFailed = (
  state: ITrainingProgramState,
  { payload }: PayloadAction<IError>
) => {
  state.loading = false;
  state.error = payload;
  state.requestState = "error";
};

const TrainingProgramSlice = createSlice({
  name: "trainingProgram",
  initialState,
  reducers: {
    getTrainingProgramStart: _getTrainingProgramStart,
    getTrainingProgramSuccess: _getTrainingProgramSuccess,
    getTrainingProgramFailed: _getTrainingProgramFailed,
    resetTrainingProgram: _resetTrainingProgram,
    resetTrainingProgramError: _resetTrainingProgramError,
    createTrainingProgramStart: _createTrainingProgramStart,
    createTrainingProgramSuccess: _createTrainingProgramSuccess,
    createTrainingProgramFailed: _createTrainingProgramFailed,
    updateTrainingProgramStart: _updateTrainingProgramStart,
    updateTrainingProgramSuccess: _updateTrainingProgramSuccess,
    updateTrainingProgramFailed: _updateTrainingProgramFailed,
    deleteTrainingProgramStart: _deleteTrainingProgramStart,
    deleteTrainingProgramSuccess: _deleteTrainingProgramSuccess,
    deleteTrainingProgramFailed: _deleteTrainingProgramFailed,

    removeWorkoutFromProgramStart: _removeWorkoutFromProgramStart,
    removeWorkoutFromProgramSuccess: _removeWorkoutFromProgramSuccess,
    removeWorkoutFromProgramFailed: _removeWorkoutFromProgramFailed,

    toggleEnableWorkoutInTrainingProgramStart:
      _toggleEnableWorkoutInTrainingProgramStart,
    toggleEnableWorkoutInTrainingProgramSuccess:
      _toggleEnableWorkoutInTrainingProgramSuccess,
    toggleEnableWorkoutInTrainingProgramFailed:
      _toggleEnableWorkoutInTrainingProgramFailed,

    overrideWorkoutInTrainingProgramStart:
      _overrideWorkoutInTrainingProgramStart,
    overrideWorkoutInTrainingProgramSuccess:
      _overrideWorkoutInTrainingProgramSuccess,
    overrideWorkoutInTrainingProgramFailed:
      _overrideWorkoutInTrainingProgramFailed,

    copyWorkoutInTrainingProgramStart: _copyWorkoutInTrainingProgramStart,
    copyWorkoutInTrainingProgramSuccess: _copyWorkoutInTrainingProgramSuccess,
    copyWorkoutInTrainingProgramFailed: _copyWorkoutInTrainingProgramFailed,

    getTrainingProgramActiveUsersStart: _getTrainingProgramActiveUsersStart,
    getTrainingProgramActiveUsersSuccess: _getTrainingProgramActiveUsersSuccess,
    getTrainingProgramActiveUsersFailed: _getTrainingProgramActiveUsersFailed,
    resetTrainingProgramActiveUsers: _resetTrainingProgramActiveUsers,

    updateWorkoutsTableState: _updateWorkoutsTableState,
    resetTrainingProgramWorkoutsTableState:
      _resetTrainingProgramWorkoutsTableState,

    getGroupsStart: _getGroupsStart,
    getGroupsSuccess: _getGroupsSuccess,
    getGroupsFailed: _getGroupsFailed,
  },
});

export const {
  getTrainingProgramStart,
  getTrainingProgramSuccess,
  getTrainingProgramFailed,
  resetTrainingProgram,
  resetTrainingProgramError,
  createTrainingProgramStart,
  createTrainingProgramSuccess,
  createTrainingProgramFailed,
  updateTrainingProgramStart,
  updateTrainingProgramSuccess,
  updateTrainingProgramFailed,
  deleteTrainingProgramStart,
  deleteTrainingProgramSuccess,
  deleteTrainingProgramFailed,

  removeWorkoutFromProgramStart,
  removeWorkoutFromProgramSuccess,
  removeWorkoutFromProgramFailed,

  toggleEnableWorkoutInTrainingProgramStart,
  toggleEnableWorkoutInTrainingProgramSuccess,
  toggleEnableWorkoutInTrainingProgramFailed,

  overrideWorkoutInTrainingProgramStart,
  overrideWorkoutInTrainingProgramSuccess,
  overrideWorkoutInTrainingProgramFailed,

  copyWorkoutInTrainingProgramStart,
  copyWorkoutInTrainingProgramSuccess,
  copyWorkoutInTrainingProgramFailed,

  getTrainingProgramActiveUsersStart,
  getTrainingProgramActiveUsersSuccess,
  getTrainingProgramActiveUsersFailed,
  resetTrainingProgramActiveUsers,

  updateWorkoutsTableState,
  resetTrainingProgramWorkoutsTableState,

  getGroupsStart,
  getGroupsSuccess,
  getGroupsFailed,
} = TrainingProgramSlice.actions;
export default TrainingProgramSlice.reducer;
